import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from '../../api/insolita';
import { Booking, BookingStatus, DayAvailability, PaymentStatus, Tour } from '../../api/types';

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const STATUS_STYLE: Record<BookingStatus, string> = {
  CONFIRMED: 'bg-green-100 text-green-700',
  WAITLISTED: 'bg-amber-100 text-amber-700',
  PENDING: 'bg-neutral-100 text-neutral-500',
  CANCELLED: 'bg-red-100 text-red-600 line-through',
};
const STATUS_LABEL: Record<BookingStatus, string> = {
  CONFIRMED: 'Confermata', WAITLISTED: "Lista d'attesa", PENDING: 'In attesa email', CANCELLED: 'Annullata',
};
const PAY_OPTIONS: PaymentStatus[] = ['PENDING', 'PAID', 'FAILED', 'REFUNDED', 'NOT_REQUIRED'];

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const weekdayMon = (d: Date) => (d.getDay() + 6) % 7;
const fmt = (d?: string) => (d ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const FlexibleAvailability: React.FC<{ tour: Tour }> = ({ tour }) => {
  const today = useMemo(() => { const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), t.getDate()); }, []);
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [days, setDays] = useState<DayAvailability[]>([]);
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [editDay, setEditDay] = useState<{ date: string; capacity: number } | null>(null);

  const [bulk, setBulk] = useState({ from: '', to: '', capacity: 20 });

  const monthStart = useMemo(() => new Date(cursor.getFullYear(), cursor.getMonth(), 1), [cursor]);
  const monthEnd = useMemo(() => new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0), [cursor]);

  const loadMonth = () => {
    setLoading(true);
    adminApi.availability.byTour(tour.id, iso(monthStart), iso(monthEnd))
      .then(setDays)
      .finally(() => setLoading(false));
  };
  const loadBookings = () => { adminApi.bookings.byTour(tour.id).then(setBookings); };

  useEffect(() => {
    loadMonth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour.id, cursor]);
  useEffect(() => {
    loadBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour.id]);

  const byDate = useMemo(() => new Map(days.map((d) => [d.date, d])), [days]);

  const applyBulk = async () => {
    if (!bulk.from || !bulk.to) return;
    await adminApi.availability.setRange(tour.id, bulk.from, bulk.to, Number(bulk.capacity));
    loadMonth();
  };
  const saveDay = async () => {
    if (!editDay) return;
    await adminApi.availability.setDay(tour.id, editDay.date, Number(editDay.capacity));
    setEditDay(null);
    loadMonth();
  };
  const removeDay = async (date: string) => {
    await adminApi.availability.removeDay(tour.id, date);
    setEditDay(null);
    loadMonth();
  };

  const act = async (fn: Promise<any>) => { await fn; loadBookings(); loadMonth(); };

  const input = 'w-full border border-neutral-200 rounded px-3 py-2 text-sm';

  // Griglia del mese
  const lead = weekdayMon(monthStart);
  const daysInMonth = monthEnd.getDate();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
  const canPrev = monthStart > new Date(today.getFullYear(), today.getMonth(), 1);

  return (
    <div>
      {/* Imposta periodo in blocco */}
      <div className="bg-white border border-neutral-200 rounded-lg p-4 mb-4">
        <p className="font-medium mb-1">Imposta disponibilità</p>
        <p className="text-sm text-neutral-500 mb-3">Assegna una capienza a tutti i giorni di un periodo (es. tutto il mese a 20 posti).</p>
        <div className="flex flex-wrap items-end gap-3">
          <div><label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">Da</label><input type="date" className={input} value={bulk.from} onChange={(e) => setBulk({ ...bulk, from: e.target.value })} /></div>
          <div><label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">A</label><input type="date" className={input} value={bulk.to} onChange={(e) => setBulk({ ...bulk, to: e.target.value })} /></div>
          <div><label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">Capienza/giorno</label><input type="number" min={0} className={input} value={bulk.capacity} onChange={(e) => setBulk({ ...bulk, capacity: Number(e.target.value) })} /></div>
          <button onClick={applyBulk} disabled={!bulk.from || !bulk.to} className="px-4 py-2 bg-[#0B0B0C] text-white rounded text-sm disabled:opacity-50">Applica</button>
        </div>
      </div>

      {/* Calendario del mese */}
      <div className="bg-white border border-neutral-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} disabled={!canPrev} className="w-8 h-8 border border-neutral-200 rounded disabled:opacity-30">‹</button>
          <span className="font-medium">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</span>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="w-8 h-8 border border-neutral-200 rounded">›</button>
        </div>
        {loading ? (
          <p className="text-neutral-400 text-sm py-6 text-center">Caricamento…</p>
        ) : (
          <div className="grid grid-cols-7 gap-1">
            {WEEKDAYS.map((w) => <div key={w} className="text-[11px] uppercase text-neutral-400 text-center pb-1">{w}</div>)}
            {cells.map((cell, i) => {
              if (!cell) return <div key={`e${i}`} />;
              const dISO = iso(cell);
              const day = byDate.get(dISO);
              const past = cell < today;
              const open = !!day && day.open && day.capacity > 0;
              return (
                <button
                  key={dISO}
                  onClick={() => setEditDay({ date: dISO, capacity: day?.capacity ?? (Number(bulk.capacity) || 20) })}
                  disabled={past}
                  className={[
                    'aspect-square rounded border text-left p-1 flex flex-col justify-between transition',
                    past ? 'opacity-40 cursor-not-allowed border-neutral-100' :
                      open ? 'border-green-200 bg-green-50 hover:border-green-400' : 'border-neutral-200 bg-neutral-50 hover:border-neutral-400',
                  ].join(' ')}
                >
                  <span className="text-xs font-medium text-neutral-700">{cell.getDate()}</span>
                  {open ? (
                    <span className="text-[10px] leading-tight text-neutral-500">
                      <b className="text-green-700">{day!.confirmedSeats}</b>/{day!.capacity}
                    </span>
                  ) : (
                    !past && <span className="text-[10px] text-neutral-300">chiuso</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        <p className="text-xs text-neutral-400 mt-3">Clicca un giorno per impostarne la capienza o chiuderlo. Il numero mostra <b>confermati/capienza</b>.</p>
      </div>

      {/* Prenotazioni del tour */}
      <h2 className="text-lg font-bold mb-3">Prenotazioni</h2>
      {bookings.length === 0 ? (
        <p className="text-sm text-neutral-400">Nessuna prenotazione.</p>
      ) : (
        <div className="overflow-x-auto bg-white border border-neutral-200 rounded-lg">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-neutral-400 text-xs uppercase border-b border-neutral-100">
                <th className="p-3">Cliente</th><th>Periodo</th><th>Persone</th><th>Stato</th><th>Pagamento</th><th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-t border-neutral-100">
                  <td className="p-3">
                    <div className="font-medium">{b.customerName}</div>
                    <div className="text-xs text-neutral-400">{b.customerEmail} · {b.customerPhone}</div>
                  </td>
                  <td className="text-xs">{fmt(b.checkInDate)} → {fmt(b.checkOutDate)}</td>
                  <td>{b.people}</td>
                  <td><span className={`text-xs px-2 py-1 rounded ${STATUS_STYLE[b.status]}`}>{STATUS_LABEL[b.status]}</span></td>
                  <td>
                    <select value={b.paymentStatus} onChange={(e) => act(adminApi.bookings.setPayment(b.id, e.target.value as PaymentStatus))}
                      className="text-xs border border-neutral-200 rounded px-1 py-1 bg-white">
                      {PAY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </td>
                  <td className="space-x-2 pr-3">
                    {b.status !== 'CONFIRMED' && b.status !== 'CANCELLED' && (
                      <button onClick={() => act(adminApi.bookings.confirm(b.id))} className="text-xs text-green-600 hover:underline">Conferma</button>
                    )}
                    {b.status !== 'CANCELLED' && (
                      <button onClick={() => act(adminApi.bookings.cancel(b.id))} className="text-xs text-red-500 hover:underline">Annulla</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Editor singolo giorno */}
      {editDay && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setEditDay(null)}>
          <div className="bg-white rounded-lg w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold mb-1">{fmt(editDay.date)}</h2>
            <p className="text-sm text-neutral-500 mb-4">Imposta la capienza di questa giornata, oppure chiudila.</p>
            <label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">Capienza</label>
            <input type="number" min={0} className={input} value={editDay.capacity} onChange={(e) => setEditDay({ ...editDay, capacity: Number(e.target.value) })} />
            <div className="flex justify-between items-center mt-6">
              <button onClick={() => removeDay(editDay.date)} className="text-sm text-red-500 hover:underline">Chiudi giorno</button>
              <div className="flex gap-3">
                <button onClick={() => setEditDay(null)} className="px-4 py-2 text-sm text-neutral-500">Annulla</button>
                <button onClick={saveDay} className="px-4 py-2 bg-[#0B0B0C] text-white rounded text-sm">Salva</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlexibleAvailability;
